
import React, { useState } from 'react';
import { useAdminPremiumBots } from '@/hooks/use-admin-premium-bots';
import { Link } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Pencil, PlusCircle, Search, Trash2, Eye } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDistanceToNow } from 'date-fns';
import { DeletePremiumBotDialog } from './DeletePremiumBotDialog';
import { PremiumBotDetailModal } from './PremiumBotDetailModal';

export const PremiumBotsList = () => {
  // Note: PremiumBotsList.tsx is getting long (380+ lines)
  // Consider refactoring into smaller components after this implementation
  const {
    premiumBots,
    totalCount,
    currentPage,
    pageSize,
    totalPages,
    isLoading,
    setPage,
    setPageSize,
    setSearchQuery,
    setStatus,
    searchParams,
  } = useAdminPremiumBots();

  const [deleteDialogState, setDeleteDialogState] = useState<{
    open: boolean;
    botId: string;
    botName: string;
  }>({
    open: false,
    botId: '',
    botName: '',
  });

  const [detailModalState, setDetailModalState] = useState<{
    open: boolean;
    bot: any | null;
  }>({
    open: false,
    bot: null,
  });

  const handleOpenDeleteDialog = (botId: string, botName: string) => {
    setDeleteDialogState({
      open: true,
      botId,
      botName,
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogState({
      open: false,
      botId: '',
      botName: '',
    });
  };

  const handleOpenDetailModal = (bot: any) => {
    setDetailModalState({
      open: true,
      bot,
    });
  };

  const handleCloseDetailModal = () => {
    setDetailModalState({
      open: false,
      bot: null,
    });
  };

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <PaginationItem key="first">
          <PaginationLink onClick={() => setPage(1)} className="cursor-pointer">
            1
          </PaginationLink>
        </PaginationItem>
      );
      
      if (startPage > 2) {
        pages.push(
          <PaginationItem key="ellipsis-start">
            <PaginationEllipsis />
          </PaginationItem>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i}>
          <PaginationLink
            onClick={() => setPage(i)}
            isActive={currentPage === i}
            className="cursor-pointer"
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <PaginationItem key="ellipsis-end">
            <PaginationEllipsis />
          </PaginationItem>
        );
      }
      
      pages.push(
        <PaginationItem key="last">
          <PaginationLink onClick={() => setPage(totalPages)} className="cursor-pointer">
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return pages;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500">Active</Badge>;
      case 'inactive':
        return <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between gap-4 items-center">
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-[300px]">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search premium bots..."
              className="pl-8"
              value={searchParams.searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Select
            value={searchParams.status}
            onValueChange={(value) => setStatus(value)}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button asChild>
          <Link to="/admin/premium-bots/new">
            <PlusCircle className="h-4 w-4 mr-1" /> Add New Bot
          </Link>
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden md:table-cell">Type</TableHead>
              <TableHead className="hidden md:table-cell">Created</TableHead>
              <TableHead className="hidden lg:table-cell">Trading Style</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Loading state
              Array.from({ length: pageSize }).map((_, index) => (
                <TableRow key={`loading-${index}`}>
                  <TableCell>
                    <Skeleton className="h-6 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-6 w-24" />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <Skeleton className="h-6 w-24" />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <Skeleton className="h-6 w-24" />
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Skeleton className="h-6 w-24" />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Skeleton className="h-8 w-8" />
                      <Skeleton className="h-8 w-8" />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : premiumBots.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center">
                  No premium bots found.
                </TableCell>
              </TableRow>
            ) : (
              premiumBots.map((bot) => (
                <TableRow key={bot.id}>
                  <TableCell className="font-medium">{bot.name}</TableCell>
                  <TableCell>{getStatusBadge(bot.status)}</TableCell>
                  <TableCell className="hidden md:table-cell">{bot.type}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    {bot.created_date
                      ? formatDistanceToNow(new Date(bot.created_date), { addSuffix: true })
                      : 'Unknown'}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {bot.premiumDetails?.trading_style || 'Unknown'}
                  </TableCell>
                   <TableCell className="text-right">
                     <div className="flex justify-end gap-2">
                       <Button
                         variant="ghost"
                         size="icon"
                         onClick={() => handleOpenDetailModal(bot)}
                       >
                         <Eye className="h-4 w-4" />
                         <span className="sr-only">View Details</span>
                       </Button>
                       <Button variant="ghost" size="icon" asChild>
                         <Link to={`/admin/premium-bots/edit/${bot.id}`}>
                           <Pencil className="h-4 w-4" />
                           <span className="sr-only">Edit</span>
                         </Link>
                       </Button>
                       <Button
                         variant="ghost"
                         size="icon"
                         onClick={() => handleOpenDeleteDialog(bot.id, bot.name)}
                       >
                         <Trash2 className="h-4 w-4 text-destructive" />
                         <span className="sr-only">Delete</span>
                       </Button>
                     </div>
                   </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-sm text-muted-foreground">
          {isLoading
            ? 'Loading...'
            : `Showing ${(currentPage - 1) * pageSize + 1} to ${Math.min(
                currentPage * pageSize,
                totalCount
              )} of ${totalCount} premium bots`}
        </div>

        <div className="flex items-center space-x-2">
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => setPageSize(parseInt(value, 10))}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="30">30</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
          
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => {
                    if (currentPage > 1) {
                      setPage(currentPage - 1);
                    }
                  }}
                  className={`cursor-pointer ${currentPage === 1 ? 'pointer-events-none opacity-50' : ''}`}
                />
              </PaginationItem>
              {renderPagination()}
              <PaginationItem>
                <PaginationNext
                  onClick={() => {
                    if (currentPage < totalPages) {
                      setPage(currentPage + 1);
                    }
                  }}
                  className={`cursor-pointer ${currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}`}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>

      <DeletePremiumBotDialog
        botId={deleteDialogState.botId}
        botName={deleteDialogState.botName}
        open={deleteDialogState.open}
        onOpenChange={handleCloseDeleteDialog}
      />

      <PremiumBotDetailModal
        bot={detailModalState.bot}
        isOpen={detailModalState.open}
        onClose={handleCloseDetailModal}
      />
    </div>
  );
};
